/**
 * Locale-aware number parsing for the public form's DOUBLE / CURRENCY inputs.
 *
 * SYNCED WITH main-frontend/src/lib/utils/currency.ts — the parsing half
 * (parseLocaleNumber / localeSeparators / disambiguateSingle /
 * toEditableLocaleString). Keep byte-equivalent; the parity test
 * `locale-number.test.ts` locks a fixed vector against the canonical repo.
 *
 * The form STORES canonical dot-decimal (backend does Number()/Double.parseDouble),
 * so the guest types in their locale ("3.519,50") and we convert to "3519.5"
 * before submit. The DECIMAL separator is never grouping; only a lone GROUP
 * separator with exactly 3 trailing digits is disambiguated as a thousands group
 * (so de "3.519" → 3519, "3,5" → 3.5).
 */

const separatorCache = new Map<string, { group: string; decimal: string }>();
function localeSeparators(locale: string): { group: string; decimal: string } {
  const cached = separatorCache.get(locale);
  if (cached) return cached;
  let group = ",";
  let decimal = ".";
  try {
    const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
    group = parts.find((p) => p.type === "group")?.value ?? group;
    decimal = parts.find((p) => p.type === "decimal")?.value ?? decimal;
  } catch {
    /* keep en-US defaults */
  }
  const sep = { group, decimal };
  separatorCache.set(locale, sep);
  return sep;
}

/** Treat a lone separator as grouping when it splits the number into clean
 *  thousand-groups, else as a decimal point. */
function disambiguateSingle(s: string, sep: string): string {
  const occurrences = s.split(sep).length - 1;
  const escaped = sep.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  if (occurrences === 1) {
    const after = s.split(sep)[1] ?? "";
    if (/^\d{3}$/.test(after)) return s.replace(new RegExp(escaped), "");
    return s.replace(new RegExp(escaped), ".");
  }
  return s.split(sep).join("");
}

/**
 * Parse a user-typed amount in `locale` into a canonical JS number, or null
 * when there are no digits / it isn't finite. KEEP IN SYNC with currency.ts.
 */
export function parseLocaleNumber(
  input: string,
  locale: string,
): number | null {
  if (input == null) return null;
  let s = String(input).trim();
  if (s === "") return null;

  const { group, decimal } = localeSeparators(locale);

  s = s.replace(/[^\d.,  '\s+-]/g, "");
  s = s.replace(/[\s  ']/g, "");
  if (s === "" || s === "-" || s === "+") return null;

  const hasGroup = group && s.includes(group);
  const hasDecimal = s.includes(decimal);

  if (hasGroup && hasDecimal) {
    s = s.split(group).join("");
    s = s.replace(decimal, ".");
  } else if (hasDecimal && !hasGroup) {
    // Decimal separator is NEVER grouping → just normalise to a dot.
    s = s.split(decimal).join(".");
  } else if (hasGroup && !hasDecimal) {
    s = disambiguateSingle(s, group);
  }

  s = s.replace(/[^\d.+-]/g, "");
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/**
 * Render a canonical number as an EDITABLE locale string (locale decimal sep,
 * NO grouping) for prefilling a text input so a re-parse round-trips faithfully
 * (canonical "12.345" would otherwise be misread as 12345 by a de guest).
 */
export function toEditableLocaleString(value: number, locale: string): string {
  if (!Number.isFinite(value)) return "";
  try {
    return new Intl.NumberFormat(locale, {
      useGrouping: false,
      maximumFractionDigits: 20,
    }).format(value);
  } catch {
    return String(value);
  }
}
